import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Eye, CalendarClock, IndianRupee } from "lucide-react";

/**
 * Phase 2 (Jul 2026) — THE one Custom Studio moment on the homepage.
 *
 * Replaces the scattered custom pushes (dismissible black promo strip +
 * editorial dept card + hero competition) with a single confident card.
 * Copy approved by owner: benefit-led, fear-killing, price-anchored.
 * The header pill remains the persistent entry point site-wide.
 */
function FactTile({ Icon, title, sub }) {
    return (
        <div className="flex items-start gap-3.5 rounded-2xl bg-ivory/[0.07] ring-1 ring-ivory/15 px-4 py-3.5">
            <span className="mt-0.5 w-8 h-8 rounded-full bg-rosegold/15 ring-1 ring-rosegold/30 flex items-center justify-center flex-shrink-0">
                <Icon size={14} className="text-rosegold" />
            </span>
            <span>
                <span className="block text-[13.5px] font-semibold text-ivory leading-tight">{title}</span>
                <span className="block text-[11.5px] text-ivory/70 mt-0.5 leading-snug">{sub}</span>
            </span>
        </div>
    );
}

export default function CustomStudioTeaser() {
    return (
        <section className="mb-12 lg:mb-24 animate-fade-up" data-testid="custom-studio-teaser">
            <Link
                to="/custom-studio"
                className="group relative block overflow-hidden rounded-3xl bg-gradient-to-br from-[#241A1B] via-wine to-[#4A1F29] text-ivory
                           px-6 py-8 sm:px-10 sm:py-10 lg:px-14 lg:py-12 ring-1 ring-rosegold/25
                           shadow-[0_18px_40px_-20px_rgba(107,44,57,0.45)] hover:shadow-[0_22px_48px_-18px_rgba(107,44,57,0.55)] transition-shadow"
            >
                {/* soft gold glow */}
                <span aria-hidden className="absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-30 pointer-events-none"
                    style={{ background: "radial-gradient(circle, rgba(201,164,122,0.5), transparent 70%)" }} />

                {/* Phase 2.1 — desktop composes the full canvas: copy left, the
                    three custom-order answers as a vertical fact column right
                    (the wine panel previously ran empty past ~50% width). */}
                <div className="relative grid lg:grid-cols-[1.25fr_1fr] gap-8 lg:gap-14 items-center">
                    <div>
                        <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-rosegold font-bold mb-3">
                            <Sparkles size={12} /> Your Custom Studio
                        </p>
                        <h2 className="font-display text-[1.65rem] sm:text-[2.3rem] lg:text-[2.6rem] leading-[1.08] font-light max-w-2xl">
                            Can&rsquo;t find it ready-made? <span className="italic text-rosegold">Design it.</span>
                        </h2>
                        <p className="mt-3 text-[13px] sm:text-[15px] text-ivory/85 max-w-xl leading-relaxed">
                            Pick the outfit, fabric and every detail — then see it on you
                            <strong className="text-ivory"> before a single thread is cut</strong>.
                        </p>

                        {/* Mobile/tablet: compact chips inline under the copy */}
                        <div className="mt-5 flex flex-wrap gap-2 lg:hidden" data-testid="custom-teaser-chips">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ivory/10 ring-1 ring-ivory/20 text-[11px] font-semibold">
                                <IndianRupee size={11} className="text-rosegold" /> From ₹4,500
                            </span>
                            {/* Short label so the 3 chips sit in 2 tidy rows at 375
                                (the copy above already says "see it on you before a
                                single thread is cut"; desktop fact tiles keep the
                                full line) */}
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ivory/10 ring-1 ring-ivory/20 text-[11px] font-semibold">
                                <Eye size={11} className="text-rosegold" /> AI preview first
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ivory/10 ring-1 ring-ivory/20 text-[11px] font-semibold">
                                <CalendarClock size={11} className="text-rosegold" /> Ready in 2–4 weeks
                            </span>
                        </div>

                        <span className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-ivory text-ink text-[11px] font-bold uppercase tracking-[0.18em] group-hover:bg-rosegold group-hover:text-ink transition-colors" data-testid="custom-teaser-cta">
                            Start designing <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                        </span>
                    </div>

                    {/* Desktop: the three answers as an elegant fact column */}
                    <div className="hidden lg:flex flex-col gap-3" data-testid="custom-teaser-facts">
                        <FactTile Icon={IndianRupee} title="From ₹4,500" sub="Fabric + stitching, priced live as you design" />
                        <FactTile Icon={Eye} title="AI preview before we stitch" sub="See it on a model — or on your own photo" />
                        <FactTile Icon={CalendarClock} title="Ready in 2–4 weeks" sub="Hand-stitched in our boutique, never outsourced" />
                    </div>
                </div>
            </Link>
        </section>
    );
}
